import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import { ReactComponent as HyperlinkIcon } from '@/assets/icons/hyperlink.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { BPArticleReel } from '@/types/resources';
import { api } from '@/utils/api';

import Status from './Status';

const HyperLink = styled('a').attrs({
  rel: 'noreferrer noopener',
  target: '_blank',
})`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  svg {
    margin-right: 6px;
    width: 14px;
    flex-shrink: 0;
  }
  > span {
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
`;

interface Props {
  reel: BPArticleReel;
}

const TableRow: FC<Props> = ({ reel }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      await api.delete(`/v1/bp-article-reels/${reel.id}`);

      await queryClient.invalidateQueries(['bp_article_reels']);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const onOpenVideo = () => {
    window.open(reel.video?.versions?.original, '_blank');
  };

  const slug = useMemo(() => {
    const url = new URL(reel.link);
    return url.pathname.replace(/^\/|\/$/g, '');
  }, [reel.link]);

  return (
    <tr>
      <Td>
        <HyperLink href={reel.link}>
          <HyperlinkIcon />
          <span>{slug}</span>
        </HyperLink>
      </Td>
      <Td>{reel.imagesCount}</Td>
      <Td>
        <Status status={reel.status} />
      </Td>
      <Td>{reel.owner ?? '-'}</Td>
      <Td>
        {DateTime.fromISO(reel.createdAt).toFormat('dd/LL/yyyy')}
        <br />
        <Text as="span" $color="gray" $type="extraSmall">
          {DateTime.fromISO(reel.createdAt).toFormat('HH:mm')}
        </Text>
      </Td>
      <Td>{reel.error ?? '-'}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              Delete
            </ActionsDropdownButton>
            {!!reel.video && (
              <ActionsDropdownButton as="button" onClick={onOpenVideo}>
                Open Video
              </ActionsDropdownButton>
            )}
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default TableRow;
